import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    BookOpen, 
    Heart, 
    Sparkles, 
    Clock, 
    ChevronRight,
    ArrowLeft,
    CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { exercisesApi } from '../api';
import { cn } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

const categoryIcons = {
    traume: Heart,
    educatie: BookOpen,
    emotii: Sparkles
};

export function ExercisesPage() {
    const { exerciseId } = useParams();
    const { t, language } = useLanguage();
    
    // Dynamic categories with translations
    const CATEGORIES = {
        traume: { id: 'traume', name: t('traume'), description: t('traumeDesc') },
        educatie: { id: 'educatie', name: t('educatie'), description: t('educatieDesc') },
        emotii: { id: 'emotii', name: t('emotii'), description: t('emotiiDesc') }
    };
    
    const [exercises, setExercises] = useState([]);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Helper function to get translated content
    const getExerciseTitle = (exercise) => {
        return language === 'en' && exercise.title_en ? exercise.title_en : exercise.title;
    };
    
    const getExerciseDescription = (exercise) => {
        return language === 'en' && exercise.description_en ? exercise.description_en : exercise.description;
    };
    
    const getExerciseSteps = (exercise) => {
        return language === 'en' && exercise.steps_en && exercise.steps_en.length > 0 
            ? exercise.steps_en 
            : exercise.steps;
    };
    
    const getExerciseDuration = (exercise) => {
        return language === 'en' && exercise.duration_en ? exercise.duration_en : exercise.duration;
    };

    useEffect(() => {
        loadExercises();
    }, []);

    useEffect(() => {
        if (exerciseId && exercises.length > 0) {
            const exercise = exercises.find(e => e.id === exerciseId);
            if (exercise) {
                setSelectedExercise(exercise);
                setCompletedSteps([]);
            }
        }
    }, [exerciseId, exercises]);

    const loadExercises = async () => {
        setIsLoading(true);
        try {
            const data = await exercisesApi.getAll();
            setExercises(data);
        } catch (error) {
            console.error('Error loading exercises:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleStep = (stepIndex) => {
        setCompletedSteps(prev => 
            prev.includes(stepIndex)
                ? prev.filter(i => i !== stepIndex)
                : [...prev, stepIndex]
        );
    };

    const filteredExercises = selectedCategory
        ? exercises.filter(e => e.category === selectedCategory)
        : exercises;

    if (selectedExercise) {
        const category = CATEGORIES[selectedExercise.category];
        const Icon = categoryIcons[selectedExercise.category];
        const exerciseSteps = getExerciseSteps(selectedExercise);
        const allStepsCompleted = completedSteps.length === exerciseSteps.length;

        return (
            <div className="min-h-[calc(100vh-4rem)] py-8 px-4 md:px-8">
                <div className="max-w-3xl mx-auto">
                    <Link
                        to="/exercises"
                        onClick={() => setSelectedExercise(null)}
                        data-testid="back-to-exercises"
                        className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('backToExercises')}
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="bg-[#111916] rounded-2xl border border-emerald-800/40 p-8 shadow-lg mb-6">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                                    <Icon className="w-7 h-7 text-emerald-400" />
                                </div>
                                <div>
                                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-2">
                                        {category?.name}
                                    </span>
                                    <h1 className="font-heading text-2xl md:text-3xl font-medium text-emerald-50">
                                        {getExerciseTitle(selectedExercise)}
                                    </h1>
                                </div>
                            </div>

                            <p className="text-emerald-300/80 text-lg leading-relaxed mb-6">
                                {getExerciseDescription(selectedExercise)}
                            </p>

                            <div className="flex items-center gap-2 text-emerald-500">
                                <Clock className="w-4 h-4" />
                                <span>{getExerciseDuration(selectedExercise)}</span>
                            </div>
                        </div>

                        <div className="bg-[#111916] rounded-2xl border border-emerald-800/40 p-8 shadow-lg">
                            <h2 className="font-heading text-xl font-medium text-emerald-100 mb-6">
                                {t('stepsToFollow')}
                            </h2>

                            <div className="space-y-4">
                                {exerciseSteps.map((step, index) => {
                                    const isCompleted = completedSteps.includes(index);
                                    return (
                                        <motion.button
                                            key={index}
                                            onClick={() => toggleStep(index)}
                                            data-testid={`step-${index}`}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className={cn(
                                                "w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all",
                                                isCompleted
                                                    ? "bg-emerald-500/20 border-2 border-emerald-500/50"
                                                    : "bg-[#0A0F0D] border-2 border-transparent hover:bg-emerald-500/5"
                                            )}
                                        >
                                            <div className={cn(
                                                "w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors",
                                                isCompleted
                                                    ? "bg-emerald-500 text-white"
                                                    : "bg-emerald-800/50 text-emerald-400"
                                            )}>
                                                {isCompleted ? (
                                                    <CheckCircle2 className="w-5 h-5" />
                                                ) : (
                                                    <span className="text-sm font-medium">{index + 1}</span>
                                                )}
                                            </div>
                                            <p className={cn(
                                                "text-base leading-relaxed pt-1",
                                                isCompleted ? "text-emerald-300" : "text-emerald-200/80"
                                            )}>
                                                {step}
                                            </p>
                                        </motion.button>
                                    );
                                })}
                            </div>

                            {allStepsCompleted && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mt-8 p-6 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl text-center"
                                >
                                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                                    <h3 className="font-heading text-xl font-medium text-emerald-100 mb-2">
                                        {t('congratulations')}
                                    </h3>
                                    <p className="text-emerald-400/80">
                                        {t('completedAllSteps')}
                                    </p>
                                </motion.div>
                            )}
                            
                            {/* Infographic - if available */}
                            {selectedExercise.infographic_url && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-8"
                                >
                                    <img 
                                        src={selectedExercise.infographic_url} 
                                        alt={`${getExerciseTitle(selectedExercise)} - Infographic`}
                                        className="w-full rounded-xl border border-emerald-800/40 shadow-lg"
                                    />
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-4rem)] py-12 px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-12"
                >
                    <h1 className="font-heading text-3xl md:text-4xl font-medium text-emerald-50 mb-4">
                        {t('practicalExercisesTitle')}
                    </h1>
                    <p className="text-lg text-emerald-300/70 max-w-2xl mx-auto">
                        {t('exercisesDescription')}
                    </p>
                </motion.div>

                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    <button
                        onClick={() => setSelectedCategory(null)}
                        data-testid="filter-all"
                        className={cn(
                            "px-5 py-2.5 rounded-full text-sm font-medium transition-all",
                            !selectedCategory
                                ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                                : "bg-emerald-800/30 text-emerald-400 hover:bg-emerald-800/50"
                        )}
                    >
                        {t('all')}
                    </button>
                    {Object.values(CATEGORIES).map((cat) => {
                        const Icon = categoryIcons[cat.id];
                        return (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                data-testid={`filter-${cat.id}`}
                                className={cn(
                                    "flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all",
                                    selectedCategory === cat.id
                                        ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                                        : "bg-emerald-800/30 text-emerald-400 hover:bg-emerald-800/50"
                                )}
                            >
                                <Icon className="w-4 h-4" />
                                {cat.name}
                            </button>
                        );
                    })}
                </div>

                {isLoading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-emerald-900/20 rounded-2xl h-64 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredExercises.map((exercise, index) => {
                            const category = CATEGORIES[exercise.category];
                            const Icon = categoryIcons[exercise.category];
                            return (
                                <motion.div
                                    key={exercise.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Link
                                        to={`/exercises/${exercise.id}`}
                                        onClick={() => setSelectedExercise(exercise)}
                                        data-testid={`exercise-card-${exercise.id}`}
                                        className="block group"
                                    >
                                        <div className="exercise-card h-full flex flex-col">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                                                    <Icon className="w-6 h-6 text-emerald-400" />
                                                </div>
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                    {category?.name}
                                                </span>
                                            </div>

                                            <h3 className="font-heading text-lg font-medium text-emerald-100 mb-2">
                                                {getExerciseTitle(exercise)}
                                            </h3>

                                            <p className="text-emerald-400/70 text-sm leading-relaxed flex-1 mb-4">
                                                {getExerciseDescription(exercise)}
                                            </p>

                                            <div className="flex items-center justify-between pt-4 border-t border-emerald-800/40">
                                                <div className="flex items-center gap-2 text-emerald-500 text-sm">
                                                    <Clock className="w-4 h-4" />
                                                    {getExerciseDuration(exercise)}
                                                </div>
                                                <div className="flex items-center gap-1 text-emerald-400 text-sm font-medium">
                                                    {t('start')}
                                                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ExercisesPage;
